/**
 * @file     AreaMixPanelClient.tsx
 * @purpose  Homes for sale near the listing, by type: one MeterRow per
 *           property type scaled to the busiest type, with the active count
 *           as the reading, then a one-line source note.
 */
'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { MeterRow } from '@/components/hub/MeterRow';
import { hubColors } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { usePanelImpression } from '@/hooks/usePanelImpression';
import { analyticsEvents } from '@/core/constants/analytics';
import type { AreaMixResult } from '@/services/property-stats/fetchAreaPropertyMix';

interface Props {
  mix: AreaMixResult;
}

export function AreaMixPanelClient({ mix }: Props) {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement | null>(null);
  usePanelImpression(ref, analyticsEvents.propertyAreaMixViewed, {
    metadata: { radius_km: mix.radiusKm, slice_count: mix.slices.length, sample: mix.total },
  });
  const slices = [...mix.slices].sort((a, b) => b.count - a.count);
  const max = Math.max(...slices.map((s) => s.count), 1);

  return (
    <Box ref={ref}>
      <Box component="h3" sx={{ m: 0, mb: 1, fontSize: 17, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
        {t('listingDetail.areaMix.title', { defaultValue: 'Homes for sale nearby, by type' })}
      </Box>
      {slices.map((s) => (
        <MeterRow
          key={s.id}
          label={s.label}
          value={s.count}
          max={max}
          valueText={`${s.count.toLocaleString('en-CA')} ${t('listingDetail.areaMix.forSale', { defaultValue: 'for sale' })}`}
          tone="primary"
        />
      ))}
      <Box component="p" sx={{ m: 0, mt: 3, fontSize: 13, color: hubColors.textSecondary }}>
        {t('listingDetail.areaMix.source', {
          defaultValue: 'Active listings within {{radius}} km of this home.',
          radius: mix.radiusKm,
        })}
      </Box>
    </Box>
  );
}
